/**
 * <s-makeup-studio>: "My Makeup Studio". The shopper picks a look and a budget; the component fills a
 * virtual makeup table with in-stock products from the makeup collection (one collection per table spot),
 * lets her swap or remove any piece, keeps a live total against the budget, and adds the kit to the cart.
 * Feeds come from templates/collection.makeup-studio.liquid; every URL arrives through data attributes.
 */
import { formatMoney, openCartDrawer } from '@theme/s-cart-add';

/** Slots each look puts on the table, and title words that make a product fit the look. */
/** Names written on orders so the merchant can see which sales came from the studio (admin-facing, English). */
const LOOK_LABELS = { natural: 'Natural', soft_glam: 'Soft Glam', full_glam: 'Full Glam', party: 'Party' };

/** Skin type from the quiz nudges face-base finishes. */
const SKIN = {
  dry: ['dewy', 'hydra', 'hydrating', 'glow', 'luminous', 'moist', 'radiant', 'satin', 'serum', 'nourishing', 'ترطيب', 'مرطب', 'لامع'],
  oily: ['matte', 'mattifying', 'oil', 'control', 'pore', 'poreless', 'longwear', '24h', 'stay', 'مطفي', 'مات'],
};
const SKIN_SLOTS = ['primer', 'foundation', 'concealer', 'powder'];
const QUIZ_LOOK = { work: 'natural', outing: 'soft_glam', wedding: 'full_glam', party: 'party' };
const LADDER = ['natural', 'soft_glam', 'full_glam'];

/** Before she picks a look: nothing is preselected and every spot is hers to fill. */
const NO_LOOK = { slots: [], words: [], avoid: [] };
const LOOKS = {
  natural: {
    slots: ['concealer', 'blush', 'mascara', 'lips'],
    words: ['nude', 'natural', 'beige', 'light', 'sheer', 'tint', 'balm', 'care', 'soft', 'peach', 'brown', 'bb', 'cc', 'fresh', 'vanilla', 'ivory', 'hydra', 'lip care', 'نود', 'طبيعي', 'بيج', 'فاتح', 'خوخي', 'بني', 'مرطب', 'بلسم', 'ناعم'],
    avoid: ['glitter', 'metallic', 'black', 'red', 'dark', 'neon'],
  },
  soft_glam: {
    slots: ['foundation', 'concealer', 'blush', 'highlighter', 'eyeshadow', 'mascara', 'lips'],
    words: ['pink', 'rose', 'nude', 'soft', 'glow', 'dewy', 'luminous', 'satin', 'peach', 'coral', 'mauve', 'pinky', 'baked', 'blossom', 'radiant', 'وردي', 'روز', 'نود', 'ناعم', 'مشرق', 'خوخي', 'مرجاني', 'لامع', 'توهج'],
    avoid: ['black', 'neon'],
  },
  full_glam: {
    slots: ['primer', 'foundation', 'concealer', 'powder', 'blush', 'highlighter', 'brows', 'eyeshadow', 'eyeliner', 'mascara', 'lip_liner', 'lips'],
    words: ['matte', 'long', 'lasting', '24h', '24hr', 'waterproof', 'full', 'volume', 'red', 'berry', 'palette', 'infallible', 'pro', 'fix', 'wear', 'ultra', 'stay', 'مطفي', 'مات', 'يدوم', 'ساعة', 'مقاوم', 'كثافة', 'أحمر ناري', 'باليت', 'تغطية', 'ثابت'],
    avoid: ['sheer', 'balm', 'tint'],
  },
  party: {
    slots: ['primer', 'foundation', 'powder', 'blush', 'highlighter', 'eyeshadow', 'eyeliner', 'mascara', 'lip_liner', 'lips'],
    words: ['glitter', 'shimmer', 'metallic', 'gold', 'red', 'glam', 'sparkle', 'dark', 'black', 'volume', 'bold', 'berry', 'plum', 'wine', 'glow', 'waterproof', 'night', 'lash', 'جليتر', 'شيمر', 'لامع', 'ذهبي', 'أحمر ناري', 'أسود', 'كثافة', 'مقاوم', 'نبيتي', 'بنفسجي', 'توهج'],
    avoid: ['nude', 'sheer', 'natural'],
  },
};

/** Share of the budget each slot aims for, so one pricey piece never eats the whole kit. */
const WEIGHTS = { primer: 0.8, highlighter: 0.8, brows: 0.6, lip_liner: 0.5, foundation: 1.4, concealer: 1.1, powder: 1, blush: 0.9, eyeshadow: 1.1, eyeliner: 0.6, mascara: 0.8, lips: 0.8 };
/** Avoid words that don't apply to a spot (black is the normal mascara and eyeliner color). */
const AVOID_EXEMPT = { mascara: ['black', 'أسود'], eyeliner: ['black', 'أسود'] };
/** Tools and accessories that sit in category collections but are not the spot's product. Tag a product studio-exclude to hide it too. */
const ACCESSORY_WORDS = ['sponge', 'blender', 'brush', 'brushes', 'puff', 'applicator', 'sharpener', 'remover', 'اسفنجة', 'إسفنجة', 'فرشاة', 'فرشة', 'بيوتي بلندر', 'مزيل'];
/** Shade words per skin tone, matched as whole words in titles and variant names. Steers the face-base spots only. */
const TONES = {
  light: ['ivory', 'porcelain', 'fair', 'light', 'vanilla', 'alabaster', 'cream', 'shell', 'buttercream', 'chantilly', 'pale', 'rosy', 'vanille', 'lightest', 'فاتح', 'فاتحة', 'عاجي', 'بورسلين', 'كريمي'],
  medium: ['beige', 'natural', 'nude', 'medium', 'buff', 'classic', 'linen', 'neutral', 'sable', 'porcelaine', 'بيج', 'طبيعي', 'قمحي', 'متوسط'],
  tan: ['sand', 'golden', 'honey', 'warm', 'tan', 'caramel', 'sun', 'bronze', 'toffee', 'ذهبي', 'عسلي', 'حنطي', 'رملي', 'كراميل'],
  deep: ['deep', 'dark', 'mocha', 'cocoa', 'espresso', 'chestnut', 'amber', 'coffee', 'mahogany', 'rich', 'غامق', 'داكن', 'أسمر', 'سمرا', 'موكا'],
};
const TONE_SLOTS = ['foundation', 'concealer', 'powder', 'highlighter'];
const STEPS_FOR_SHUFFLE = 12;
const STORAGE_KEY = 'elbeaute-makeup-studio:v1';
const SAVE_COMMANDS = new Set(['--quiz-answer', '--look', '--budget', '--tone', '--shuffle', '--pick', '--remove', '--fill', '--suggest']);
/** Feeds read every page of each spot's collection (250 products a page), a few pages at a time. */
const MAX_PAGES = 20;
const PAGE_CONCURRENCY = 3;
const PICKER_PAGE = 48;

class MakeupStudio extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    const decoder = document.createElement('textarea');
    this.copy = JSON.parse(this.querySelector('[data-copy]').textContent, (key, value) => {
      if (typeof value !== 'string') return value;
      decoder.innerHTML = value;
      return decoder.value;
    });
    this.slotConfig = JSON.parse(this.querySelector('[data-slot-config]').textContent);
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    // ?look=natural|soft_glam|full_glam|party (from the homepage swatches) preselects a look.
    const params = new URLSearchParams(window.location.search);
    const urlLook = params.get('look');
    this.state = {
      look: LOOKS[urlLook] ? urlLook : null,
      budget: params.has('budget') ? Math.max(0, Number(params.get('budget')) || 0) * 100 : Number(this.dataset.budget || 0),
      tone: TONES[params.get('tone')] ? params.get('tone') : 'any',
      skin: null,
      shuffle: 0,
    };
    this.catalog = {};
    this.kit = {};
    // ?kit=slot.variantId~slot.variantId (from a shared link) rebuilds that exact kit once products load.
    this.shared = new Map(
      (params.get('kit') || '').split('~').map((pair) => pair.split('.')).filter(([slot, id]) => slot && /^\d+$/.test(id || ''))
    );
    this.loaded = false;
    // Choices show as picked only once she (or a saved kit / shared link) picks them.
    this.chosen = { budget: params.has('budget'), tone: params.has('tone') };
    if (!params.has('kit') && !params.has('look')) this.restore();

    this.trays = Object.fromEntries([...this.querySelectorAll('[data-tray]')].map((tray) => [tray.dataset.tray, tray]));
    this.picker = this.querySelector('[data-picker]');
    this.pickerGrid = this.querySelector('[data-picker-grid]');
    this.pickerHeading = this.querySelector('[data-picker-heading]');
    this.optionTemplate = this.querySelector('[data-option-template]');
    this.status = this.querySelector('[data-status]');
    this.addAllButtons = [...this.querySelectorAll('[data-add-all]')];
    this.kitMin = Number(this.dataset.kitMin || 0);
    this.kitPercent = Number(this.dataset.kitPercent || 0);
    this.freeShip = Number(this.dataset.freeShip || 0);
    this.kitCode = (this.dataset.kitCode || '').trim();

    this.addEventListener('command', (event) => this.onCommand(event), { signal });
    this.bindSheetDrag(signal);
    let searchTimer = 0;
    this.picker?.querySelector('[data-picker-search]')?.addEventListener('input', (event) => {
      window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(() => this.filterPicker(event.target.value), 150);
    }, { signal });
    // Tap outside the picker sheet closes it.
    this.picker?.addEventListener('click', (event) => {
      if (event.target === this.picker) this.picker.close();
    }, { signal });
    const quiz = this.querySelector('[data-quiz]');
    quiz?.addEventListener('click', (event) => {
      if (event.target === quiz) quiz.close();
    }, { signal });
    this.addEventListener('change', (event) => {
      const select = event.target.closest?.('[data-shade-select]');
      if (select) this.setShade(select.dataset.slot, select.value);
    }, { signal });
    // Browsers without Invoker Commands (older phones): route the same buttons through click.
    if (!('command' in HTMLButtonElement.prototype)) {
      this.addEventListener('click', (event) => {
        const button = event.target.closest('button[commandfor][command]');
        if (!button || !this.contains(button)) return;
        const command = button.getAttribute('command');
        const target = document.getElementById(button.getAttribute('commandfor'));
        if (command === 'close' && target instanceof HTMLDialogElement) {
          target.close();
        } else if (target === this && command.startsWith('--')) {
          this.onCommand({ command, source: button });
        }
      }, { signal });
    }
    this.syncChoices();

    this.observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.observer.disconnect();
      this.load();
    }, { rootMargin: '600px 0px' });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
  }

  money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat);
  }

  /** Deeper best-seller feed for one spot (templates/collection.makeup-studio.liquid). Rejects where alternate templates aren't served. */
  async fetchFeed(url) {
    const base = `${url}?view=makeup-studio&sort_by=best-selling`;
    const read = async (page) => {
      const response = await fetch(`${base}&page=${page}`, { signal: this.controller.signal });
      if (!response.ok) throw new Error(String(response.status));
      return JSON.parse(await response.text());
    };
    const first = await read(1);
    const items = [...first.items];
    const pages = Math.min(Number(first.pages) || 1, MAX_PAGES);
    const rest = [];
    for (let page = 2; page <= pages; page += 1) rest.push(page);
    while (rest.length) {
      const batch = rest.splice(0, PAGE_CONCURRENCY);
      const results = await Promise.all(batch.map((page) => read(page).catch((error) => {
        if (error.name === 'AbortError') throw error;
        return { items: [] };
      })));
      for (const result of results) items.push(...(result.items || []));
    }
    return items;
  }

  /** Interleave several lists (e.g. lipstick + gloss), dedupe by variant, and precompute match text and rank. */
  merge(lists) {
    const seen = new Set();
    const merged = [];
    const longest = Math.max(0, ...lists.map((list) => list.length));
    for (let i = 0; i < longest; i += 1) {
      for (const list of lists) {
        const item = list[i];
        if (!item || seen.has(item.v)) continue;
        seen.add(item.v);
        const text = `${item.t} ${item.vt || ''}`.toLowerCase();
        const tokens = new Set(text.split(/[^\p{L}\p{N}]+/u).filter(Boolean));
        if (ACCESSORY_WORDS.some((word) => (word.includes(' ') ? text.includes(word) : tokens.has(word)))) continue;
        const tags = Array.isArray(item.k) ? item.k.map((tag) => String(tag).toLowerCase()) : [];
        merged.push({ ...item, rank: merged.length, text, tokens, tags, ...this.cleanName(item) });
      }
    }
    this.guessTones(merged);
    return merged;
  }

  /**
   * Shade words in a title win (Ivory, Beige, Golden, Deep...). Many lines only number their shades
   * (Maybelline 096-248, Topface 002-008), so inside one product line a lower number counts as lighter:
   * the line's numbers are split into four bands, light to deep.
   */
  guessTones(list) {
    const lines = new Map();
    for (const item of list) {
      item.toneWords = Object.keys(TONES).filter((tone) => TONES[tone].some((word) => item.tokens.has(word)));
      item.toneGuess = null;
      const match = String(item.t).match(/(\d{2,4})(?!.*\d)/);
      if (!match) continue;
      const line = String(item.t).toLowerCase().replace(/\d+/g, '').replace(/[^\p{L}]+/gu, ' ').trim();
      if (!lines.has(line)) lines.set(line, []);
      lines.get(line).push({ item, number: Number(match[1]) });
    }
    const bands = ['light', 'medium', 'tan', 'deep'];
    for (const entries of lines.values()) {
      const numbers = [...new Set(entries.map((entry) => entry.number))].sort((a, b) => a - b);
      if (numbers.length < 3) continue;
      for (const entry of entries) {
        const position = numbers.indexOf(entry.number) / (numbers.length - 1);
        entry.item.toneGuess = bands[Math.min(3, Math.floor(position * 4))];
      }
    }
  }

  async load() {
    // 1. Products written into the page: the table fills right away, in previews too.
    let seed = {};
    try {
      seed = JSON.parse(this.querySelector('[data-seed]')?.textContent || '{}');
    } catch {
      seed = {};
    }
    const seedLists = {};
    for (const slot of Object.keys(this.slotConfig)) seedLists[slot] = (seed[slot] || []).flat();
    this.catalog = Object.fromEntries(Object.entries(seedLists).map(([slot, list]) => [slot, this.merge([list])]));
    if (Object.values(this.catalog).some((list) => list.length)) {
      this.loaded = true;
      this.toggleAttribute('data-ready', true);
      this.buildKit();
      this.applyShared();
    }

    // 2. Best-seller feeds add depth where the storefront serves them; silently skipped otherwise.
    try {
      const entries = await Promise.all(Object.entries(this.slotConfig).map(async ([slot, urls]) => {
        const lists = await Promise.all(urls.filter(Boolean).map((url) => this.fetchFeed(url).catch((error) => {
          if (error.name === 'AbortError') throw error;
          return [];
        })));
        return [slot, this.merge([...lists, seedLists[slot]])];
      }));
      const improved = entries.some(([slot, list]) => list.length > (this.catalog[slot]?.length || 0));
      if (improved) {
        this.catalog = Object.fromEntries(entries);
        this.loaded = true;
        this.toggleAttribute('data-ready', true);
        if (!this.touched) {
          this.buildKit();
        } else {
          for (const slot of Object.keys(this.trays)) this.renderTray(slot);
        }
        this.applyShared();
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
    }

    if (!this.loaded) {
      this.announce(this.copy.error);
      for (const node of this.querySelectorAll('[data-count]')) node.textContent = this.copy.error;
      for (const tray of Object.values(this.trays)) tray.dataset.state = 'none';
    }
  }

  onCommand(event) {
    this.runCommand(event);
    if (SAVE_COMMANDS.has(event.command)) {
      this.dirty = true;
      this.save();
    }
  }

  runCommand(event) {
    const source = event.source;
    const slot = source?.dataset.slot;
    switch (event.command) {
      case '--reset':
        this.reset();
        break;
      case '--look':
        this.state.look = source.dataset.look;
        this.state.shuffle = 0;
        this.syncChoices();
        this.buildKit();
        this.revealTable();
        break;
      case '--to-summary':
        this.querySelector('[data-summary-box]')?.scrollIntoView({ behavior: this.reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
        break;
      case '--budget':
        this.chosen.budget = true;
        this.state.budget = Number(source.dataset.budget);
        this.state.shuffle = 0;
        this.syncChoices();
        this.buildKit();
        break;
      case '--tone':
        this.chosen.tone = true;
        this.state.tone = source.dataset.tone;
        this.syncChoices();
        this.retone();
        break;
      case '--suggest':
        if (!this.suggestion) break;
        this.touched = true;
        this.kit[this.suggestion.slot] = this.suggestion.product;
        this.renderTray(this.suggestion.slot);
        this.renderSummary();
        break;
      case '--quiz':
        this.openQuiz();
        break;
      case '--quiz-answer':
        this.answerQuiz(source.dataset.q, source.dataset.a);
        break;
      case '--quiz-back':
        this.quizStep(Math.max(0, this.quizIndex - 1));
        break;
      case '--picker-more':
        this.renderPickerPage();
        break;
      case '--share-whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(`${this.shareMessage()}\n${this.shareUrl()}`)}`, '_blank', 'noopener');
        break;
      case '--copy-link':
        this.copyLink(source);
        break;
      case '--shuffle':
        this.state.shuffle += 1;
        this.buildKit();
        break;
      case '--change':
        this.openPicker(slot);
        break;
      case '--pick':
        this.touched = true;
        this.kit[this.pickerSlot] = this.catalog[this.pickerSlot][Number(source.dataset.index)];
        this.picker.close();
        this.renderTray(this.pickerSlot);
        this.renderSummary();
        this.trays[this.pickerSlot]?.querySelector('[data-change]')?.focus();
        break;
      case '--remove':
        this.touched = true;
        delete this.kit[slot];
        this.renderTray(slot);
        this.renderSummary();
        this.trays[slot]?.querySelector('[data-fill]')?.focus();
        break;
      case '--fill':
        this.touched = true;
        this.kit[slot] = this.choose(slot, this.slotTarget(slot, [...Object.keys(this.kit), slot]));
        this.renderTray(slot);
        this.renderSummary();
        this.trays[slot]?.querySelector('[data-change]')?.focus();
        break;
      case '--add-one':
        if (this.shadeGate([slot])) break;
        this.addToCart([this.kit[slot]], source);
        break;
      case '--add-all':
        if (this.shadeGate(Object.keys(this.kit))) break;
        this.addToCart(Object.values(this.kit).filter(Boolean), source);
        break;
      default:
    }
  }

  syncChoices() {
    for (const button of this.querySelectorAll('[data-look]')) {
      button.setAttribute('aria-pressed', String(button.dataset.look === this.state.look));
    }
    for (const button of this.querySelectorAll('[data-budget]')) {
      button.setAttribute('aria-pressed', String(this.chosen.budget && Number(button.dataset.budget) === this.state.budget));
    }
    for (const button of this.querySelectorAll('[data-tone]')) {
      button.setAttribute('aria-pressed', String(this.chosen.tone && button.dataset.tone === this.state.tone));
    }
  }

  /** Hide Shopify's default "Other" vendor and don't repeat a brand the title already starts with. */
  cleanName(item) {
    const raw = String(item.b || '').trim();
    const brand = raw && raw.toLowerCase() !== 'other' ? raw : '';
    let title = String(item.t || '');
    if (brand && title.toLowerCase().startsWith(brand.toLowerCase())) title = title.slice(brand.length).replace(/^[\s\-–|:.]+/, '').trim() || item.t;
    return { brand, name: title };
  }

  hit(product, word) {
    return word.includes(' ') ? product.text.includes(word) : product.tokens.has(word);
  }

  score(product, look, slot) {
    let score = 0;
    // Merchant tags win over guesses: look:soft_glam (or studio:soft_glam) marks a product for a look.
    if (product.tags.includes(`look:${this.state.look}`) || product.tags.includes(`studio:${this.state.look}`)) score += 8;
    for (const word of look.words) if (this.hit(product, word)) score += 3;
    const exempt = AVOID_EXEMPT[slot] || [];
    for (const word of look.avoid) if (!exempt.includes(word) && this.hit(product, word)) score -= 4;
    if (product.c > product.p) score += 1;
    if (this.state.skin && SKIN[this.state.skin] && SKIN_SLOTS.includes(slot)) {
      for (const word of SKIN[this.state.skin]) if (this.hit(product, word)) score += 3;
      const other = this.state.skin === 'dry' ? SKIN.oily : SKIN.dry;
      for (const word of other) if (this.hit(product, word)) score -= 1;
    }
    // Skin tone: favor shades named for her tone and push away shades named for another tone.
    if (this.state.tone !== 'any' && TONE_SLOTS.includes(slot)) {
      const tones = product.toneWords?.length ? product.toneWords : (product.toneGuess ? [product.toneGuess] : []);
      if (tones.includes(this.state.tone)) score += 14;
      else if (tones.length) score -= 10;
    }
    return score - product.rank * 0.04;
  }

  get look() {
    return LOOKS[this.state.look] || NO_LOOK;
  }

  ranked(slot) {
    const look = this.look;
    return [...(this.catalog[slot] || [])].sort((a, b) => this.score(b, look, slot) - this.score(a, look, slot));
  }

  slotTarget(slot, slots) {
    if (!this.state.budget) return Infinity;
    const total = slots.reduce((sum, key) => sum + (WEIGHTS[key] || 1), 0);
    return this.state.budget * ((WEIGHTS[slot] || 1) / total);
  }

  choose(slot, target) {
    const list = this.ranked(slot);
    if (!list.length) return undefined;
    const fits = list.filter((product) => product.p <= target);
    const pool = fits.length ? fits : [...list].sort((a, b) => a.p - b.p);
    const spread = Math.min(pool.length, STEPS_FOR_SHUFFLE);
    return pool[this.state.shuffle % spread];
  }

  buildKit() {
    if (!this.loaded) return;
    // No look yet: leave the table as she made it (empty on a first visit).
    if (!LOOKS[this.state.look]) {
      for (const slot of Object.keys(this.trays)) this.renderTray(slot);
      this.renderSummary();
      return;
    }
    const slots = LOOKS[this.state.look].slots.filter((slot) => this.catalog[slot]?.length);
    this.kit = {};
    for (const slot of slots) this.kit[slot] = this.choose(slot, this.slotTarget(slot, slots));
    // Tight budgets: drop the priciest extras until the kit fits, keeping at least three pieces.
    if (this.state.budget) {
      const order = ['eyeliner', 'powder', 'eyeshadow', 'concealer', 'foundation'];
      for (const slot of order) {
        if (this.total() <= this.state.budget || Object.keys(this.kit).length <= 3) break;
        if (this.kit[slot]) delete this.kit[slot];
      }
    }
    if (this.state.budget) this.useLeftover(slots);
    for (const slot of Object.keys(this.trays)) this.renderTray(slot);
    this.renderSummary();
  }

  /** Spend what's left: upgrade the cheapest pieces to better-fitting ones that still keep the kit within budget. */
  useLeftover(slots) {
    const look = this.look;
    // First bring back look pieces a tight budget dropped, if one now fits.
    for (const slot of slots) {
      if (this.kit[slot]) continue;
      const left = this.state.budget - this.total();
      const fit = this.ranked(slot).find((product) => product.p <= left);
      if (fit) this.kit[slot] = fit;
    }
    for (let round = 0; round < slots.length; round += 1) {
      const left = this.state.budget - this.total();
      if (left <= 0) return;
      const pieces = Object.entries(this.kit).filter(([, product]) => product).sort((a, b) => a[1].p - b[1].p);
      let upgraded = false;
      for (const [slot, current] of pieces) {
        const better = this.ranked(slot).find((product) => product.p > current.p && product.p - current.p <= left && this.score(product, look, slot) >= this.score(current, look, slot) - 1);
        if (better) {
          this.kit[slot] = better;
          upgraded = true;
          break;
        }
      }
      if (!upgraded) return;
    }
  }

  total(key = 'p') {
    return Object.values(this.kit).reduce((sum, product) => sum + (product ? Number(product[key] || product.p) : 0), 0);
  }

  renderTray(slot) {
    const tray = this.trays[slot];
    if (!tray) return;
    let product = this.kit[slot];
    if (!this.catalog[slot]?.length) {
      tray.dataset.state = 'none';
      return;
    }
    if (product) this.kit[slot] = this.prepared(slot, product);
    tray.dataset.state = product ? 'filled' : 'empty';
    tray.toggleAttribute('data-optional', !product && Boolean(LOOKS[this.state.look]) && !this.look.slots.includes(slot));
    tray.removeAttribute('data-needs-shade');
    if (!product) return;
    this.renderShade(slot);
    this.renderLow(tray, this.kit[slot]);
    const link = tray.querySelector('[data-link]');
    link.href = product.u;
    const img = tray.querySelector('[data-img]');
    if (product.i) {
      img.src = product.i;
      img.alt = product.a || product.t;
      img.hidden = false;
    } else {
      img.hidden = true;
    }
    tray.querySelector('[data-brand]').textContent = product.brand || '';
    tray.querySelector('[data-title]').textContent = product.vt ? `${product.name} · ${product.vt}` : product.name;
    product = this.kit[slot];
    tray.querySelector('[data-price]').textContent = product.pf || this.money(product.p);
    const compare = tray.querySelector('[data-compare]');
    compare.hidden = !(product.c > product.p);
    if (!compare.hidden) compare.querySelector('[data-compare-value]').textContent = this.money(product.c);
    if (!this.reduceMotion.matches) {
      tray.animate([{ opacity: 0.4, transform: 'translateY(6px)' }, { opacity: 1, transform: 'none' }], { duration: 260, easing: 'cubic-bezier(.2,.8,.2,1)' });
    }
  }

  renderSummary() {
    const items = Object.values(this.kit).filter(Boolean);
    const total = this.total();
    const compareTotal = items.reduce((sum, product) => sum + Math.max(Number(product.c) || 0, product.p), 0);
    const savings = compareTotal - total;
    for (const node of this.querySelectorAll('[data-total]')) node.textContent = this.money(total);
    for (const node of this.querySelectorAll('[data-count]')) node.textContent = this.copy.itemsCount.replace('__COUNT__', items.length);
    for (const node of this.querySelectorAll('[data-savings]')) {
      node.hidden = savings <= 0;
      node.textContent = this.copy.savings.replace('__AMOUNT__', this.money(savings));
    }
    const meter = this.querySelector('[data-meter]');
    const budgetLine = this.querySelector('[data-budget-line]');
    if (this.state.budget) {
      const ratio = Math.min(total / this.state.budget, 1);
      const over = total > this.state.budget;
      meter.parentElement.hidden = false;
      meter.style.inlineSize = `${Math.round(ratio * 100)}%`;
      meter.toggleAttribute('data-over', over);
      budgetLine.hidden = false;
      budgetLine.toggleAttribute('data-over', over);
      budgetLine.textContent = over
        ? this.copy.over.replace('__AMOUNT__', this.money(total - this.state.budget))
        : this.copy.remaining.replace('__AMOUNT__', this.money(this.state.budget - total));
    } else {
      meter.parentElement.hidden = true;
      budgetLine.hidden = true;
    }
    this.renderKitDiscount(items, total);
    this.renderSteps();
    this.sortTrays();
    const share = this.querySelector('[data-share]');
    if (share) share.hidden = items.length === 0;
    this.querySelector('[data-empty-kit]').hidden = items.length > 0;
    const hint = this.querySelector('[data-start-hint]');
    if (hint) hint.hidden = Boolean(LOOKS[this.state.look]) || items.length > 0;
    for (const button of this.addAllButtons) button.disabled = items.length === 0;
  }

  /** Pieces that earn the kit discount: makeup with no compare-at price, mirroring the store's automatic discount collection. */
  eligible(product) {
    return product.e === true || (product.e === undefined && !(product.c > 0));
  }

  /** Kit discount (only on pieces not already on sale) and free-shipping progress on the price after that discount. */
  renderKitDiscount(items, total) {
    let kitSaving = 0;
    const kitBox = this.querySelector('[data-kit]');
    if (kitBox && this.kitPercent > 0 && this.kitMin > 0) {
      const eligible = items.filter((product) => this.eligible(product));
      const need = this.kitMin - eligible.length;
      const on = need <= 0;
      kitBox.hidden = items.length === 0;
      kitBox.toggleAttribute('data-on', on);
      if (on) kitSaving = Math.round(eligible.reduce((sum, product) => sum + product.p, 0) * this.kitPercent / 100);
      kitBox.querySelector('[data-kit-line]').textContent = on
        ? this.copy.kitOn.replace('__PERCENT__', this.kitPercent).replace('__AMOUNT__', this.money(kitSaving))
        : this.copy.kitNeed.replace('__COUNT__', need).replace('__PERCENT__', this.kitPercent);
      const kitTotal = kitBox.querySelector('[data-kit-total]');
      kitTotal.hidden = !on;
      kitTotal.textContent = this.copy.kitTotal.replace('__AMOUNT__', this.money(total - kitSaving));
    }

    const mini = this.querySelector('[data-mini-note]');
    const ship = this.querySelector('[data-ship]');
    if (mini) {
      const shipLeft = this.freeShip ? this.freeShip - (total - kitSaving) : 0;
      mini.textContent = kitSaving > 0
        ? this.copy.kitTotal.replace('__AMOUNT__', this.money(total - kitSaving))
        : (this.freeShip && items.length ? (shipLeft > 0 ? this.copy.shipRemaining.replace('__AMOUNT__', this.money(shipLeft)) : this.copy.shipReached) : '');
      mini.hidden = !mini.textContent;
    }
    if (!ship || !this.freeShip) return;
    const net = total - kitSaving;
    const left = this.freeShip - net;
    ship.hidden = items.length === 0;
    ship.toggleAttribute('data-reached', left <= 0);
    const line = ship.querySelector('[data-ship-line]');
    line.toggleAttribute('data-reached', left <= 0);
    line.textContent = left <= 0 ? this.copy.shipReached : this.copy.shipRemaining.replace('__AMOUNT__', this.money(left));
    ship.querySelector('[data-ship-meter]').style.inlineSize = `${Math.round(Math.min(net / this.freeShip, 1) * 100)}%`;

    // Suggest the cheapest piece for an empty spot that closes the gap on its own.
    this.suggestion = null;
    if (left > 0) {
      for (const slot of Object.keys(this.trays)) {
        if (this.kit[slot]) continue;
        const pick = this.ranked(slot).slice(0, STEPS_FOR_SHUFFLE).filter((product) => product.p >= left).sort((a, b) => a.p - b.p)[0];
        if (pick && (!this.suggestion || pick.p < this.suggestion.product.p)) this.suggestion = { slot, product: pick };
      }
    }
    const suggest = ship.querySelector('[data-ship-suggest]');
    suggest.hidden = !this.suggestion;
    if (this.suggestion) {
      const { product } = this.suggestion;
      suggest.textContent = this.copy.shipSuggest.replace('__TITLE__', product.name).replace('__PRICE__', product.pf || this.money(product.p));
    }
  }

  /** Put a shared kit on the table: pieces still in stock replace the automatic picks; missing ones may arrive with the deeper feeds. */
  applyShared() {
    if (!this.shared.size) return;
    const first = !this.sharedApplied;
    const found = [];
    for (const [slot, id] of this.shared) {
      if (!this.trays[slot]) { this.shared.delete(slot); continue; }
      const product = (this.catalog[slot] || []).find((item) => String(item.v) === id || (item.vs || []).some((variant) => String(variant.v) === id));
      if (product) found.push([slot, this.withVariant(product, id, true)]);
    }
    if (first && !found.length) return;
    if (first) this.kit = {};
    for (const [slot, product] of found) {
      this.kit[slot] = product;
      this.shared.delete(slot);
    }
    this.sharedApplied = true;
    this.touched = true;
    for (const slot of Object.keys(this.trays)) this.renderTray(slot);
    this.renderSummary();
    if (first) {
      if (this.restored) {
        this.querySelector('[data-restored]').hidden = false;
      } else {
        this.announce(this.copy.sharedLoaded);
      }
    }
  }

  /** Skin tone changed: re-pick only the face-base spots (foundation, concealer, powder, highlighter); the rest of the kit stays. */
  retone() {
    if (!this.loaded) return;
    const inKit = Object.keys(this.kit).filter((slot) => this.kit[slot]);
    const look = this.look.slots;
    for (const slot of TONE_SLOTS) {
      if (!this.catalog[slot]?.length) continue;
      if (!this.kit[slot] && !look.includes(slot)) continue;
      const slots = [...new Set([...inKit, slot])];
      this.kit[slot] = this.choose(slot, this.slotTarget(slot, slots));
    }
    for (const slot of TONE_SLOTS) this.renderTray(slot);
    this.renderSummary();
  }

  /** A copy of a catalog item set to one of its variants; `picked` means the shade was chosen, not assumed. */
  withVariant(product, variantId, picked) {
    const base = product.base || product;
    const variant = (base.vs || []).find((item) => String(item.v) === String(variantId));
    if (!variant) return { ...base, base, picked: picked || !(base.vs?.length > 1) };
    return { ...base, base, v: variant.v, vt: variant.t, p: variant.p, pf: variant.pf, c: variant.c, e: variant.e, q: variant.q, picked };
  }

  /** Multi-shade pieces: preselect a shade named for her skin tone on face-base spots, otherwise ask her to choose. */
  prepared(slot, product) {
    if (product.base) return product;
    if (!(product.vs?.length > 1)) return product;
    if (this.state.tone !== 'any' && TONE_SLOTS.includes(slot)) {
      const words = TONES[this.state.tone];
      const match = product.vs.find((variant) => {
        const tokens = new Set(String(variant.t).toLowerCase().split(/[^\p{L}\p{N}]+/u));
        return words.some((word) => tokens.has(word));
      });
      if (match) return this.withVariant(product, match.v, true);
    }
    return this.withVariant(product, product.v, false);
  }

  renderShade(slot) {
    const tray = this.trays[slot];
    const product = this.kit[slot];
    const wrap = tray.querySelector('[data-shade]');
    const select = tray.querySelector('[data-shade-select]');
    if (!wrap || !select) return;
    const variants = product?.base?.vs || [];
    wrap.hidden = variants.length < 2;
    if (wrap.hidden) return;
    const options = [];
    if (!product.picked) options.push(new Option(this.copy.shadePlaceholder, '', true, true));
    for (const variant of variants) {
      options.push(new Option(variant.t, String(variant.v), false, product.picked && String(variant.v) === String(product.v)));
    }
    select.replaceChildren(...options);
  }

  setShade(slot, variantId) {
    const product = this.kit[slot];
    if (!product || !variantId) return;
    this.touched = true;
    this.kit[slot] = this.withVariant(product, variantId, true);
    this.renderTray(slot);
    this.renderSummary();
    this.trays[slot]?.querySelector('[data-shade-select]')?.focus();
    this.dirty = true;
    this.save();
  }

  /** Stops an add while a multi-shade piece still has no chosen shade, and points her to it. */
  shadeGate(slots) {
    const missing = slots.filter((slot) => this.kit[slot] && this.kit[slot].base?.vs?.length > 1 && !this.kit[slot].picked);
    for (const slot of missing) this.trays[slot]?.toggleAttribute('data-needs-shade', true);
    if (!missing.length) return false;
    const first = missing[0];
    this.announce(this.copy.pickShadeFirst.replace('__SLOT__', this.copy.slots[first]));
    const select = this.trays[first]?.querySelector('[data-shade-select]');
    select?.scrollIntoView({ block: 'center', behavior: this.reduceMotion.matches ? 'auto' : 'smooth' });
    select?.focus({ preventScroll: true });
    return true;
  }

  renderSteps() {
    const box = this.querySelector('[data-steps]');
    const list = this.querySelector('[data-steps-list]');
    const template = this.querySelector('[data-step-template]');
    if (!box || !list || !template) return;
    const fragment = document.createDocumentFragment();
    let number = 0;
    for (const slot of Object.keys(this.trays)) {
      const product = this.kit[slot];
      if (!product) continue;
      number += 1;
      const node = template.content.firstElementChild.cloneNode(true);
      node.querySelector('[data-step-number]').textContent = String(number);
      node.querySelector('[data-step-slot]').textContent = this.copy.slots[slot];
      node.querySelector('[data-step-product]').textContent = product.vt && product.picked ? `${product.name} · ${product.vt}` : product.name;
      node.querySelector('[data-step-tip]').textContent = this.copy.steps?.[slot] || '';
      fragment.append(node);
    }
    list.replaceChildren(fragment);
    box.hidden = number === 0;
  }

  /** Last kit she built on this device (look, budget, tone, pieces with chosen shades). */
  restore() {
    let saved;
    try {
      saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
    } catch {
      saved = null;
    }
    if (!saved || !Array.isArray(saved.kit) || !saved.kit.length) return;
    this.state.look = LOOKS[saved.look] ? saved.look : null;
    this.chosen = { budget: Boolean(saved.chosenBudget ?? true), tone: Boolean(saved.chosenTone ?? true) };
    this.state.budget = Math.max(0, Number(saved.budget) || 0);
    this.state.tone = TONES[saved.tone] ? saved.tone : 'any';
    this.state.skin = SKIN[saved.skin] ? saved.skin : null;
    this.shared = new Map(saved.kit.filter((pair) => Array.isArray(pair) && pair.length === 2).map(([slot, id]) => [String(slot), String(id)]));
    this.restored = true;
  }

  save() {
    if (!this.dirty || !this.loaded) return;
    const kit = Object.entries(this.kit).filter(([, product]) => product).map(([slot, product]) => [slot, String(product.v)]);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ look: this.state.look, budget: this.state.budget, tone: this.state.tone, skin: this.state.skin, chosenBudget: this.chosen.budget, chosenTone: this.chosen.tone, kit }));
    } catch {
      // Private mode or full storage: the kit just isn't remembered.
    }
  }

  reset() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore storage errors.
    }
    this.shared = new Map();
    this.restored = false;
    this.dirty = false;
    this.touched = false;
    this.state.look = null;
    this.chosen = { budget: false, tone: false };
    this.kit = {};
    this.state.budget = Number(this.dataset.budget || 0);
    this.state.tone = 'any';
    this.state.skin = null;
    this.state.shuffle = 0;
    this.querySelector('[data-restored]').hidden = true;
    this.syncChoices();
    this.buildKit();
  }

  shareUrl() {
    const url = new URL(window.location.pathname, window.location.origin);
    if (this.state.look) url.searchParams.set('look', this.state.look);
    url.searchParams.set('budget', String(Math.round(this.state.budget / 100)));
    if (this.state.tone !== 'any') url.searchParams.set('tone', this.state.tone);
    const pieces = Object.entries(this.kit).filter(([, product]) => product).map(([slot, product]) => `${slot}.${product.v}`);
    url.searchParams.set('kit', pieces.join('~'));
    return url.toString();
  }

  shareMessage() {
    const items = Object.values(this.kit).filter(Boolean);
    return this.copy.shareText.replace('__COUNT__', items.length).replace('__TOTAL__', this.money(this.total()));
  }

  async copyLink(button) {
    const label = button.querySelector('[data-label]') || button;
    const original = label.textContent;
    try {
      await navigator.clipboard.writeText(this.shareUrl());
      label.textContent = this.copy.linkCopied;
      this.announce(this.copy.linkCopied);
    } catch {
      window.prompt('', this.shareUrl());
    }
    window.setTimeout(() => { label.textContent = original; }, 2000);
  }

  openPicker(slot) {
    const list = this.ranked(slot);
    if (!list.length) return;
    this.pickerSlot = slot;
    this.pickerHeading.textContent = this.copy.pickerHeading.replace('__SLOT__', this.copy.slots[slot]);
    this.pickerAll = list;
    this.pickerList = list;
    this.pickerShown = 0;
    const search = this.picker.querySelector('[data-picker-search]');
    if (search) search.value = '';
    this.picker.querySelector('[data-picker-empty]').hidden = true;
    this.pickerGrid.replaceChildren();
    this.renderPickerPage();
    this.picker.style.translate = '';
    this.picker.showModal();
    this.picker.querySelector('[data-picker-scroll]').scrollTop = 0;
  }

  /** "Only 2 left" on pieces Shopify tracks with few units, at or under the block's threshold. */
  renderLow(node, product) {
    const badge = node.querySelector('[data-low]');
    if (!badge) return;
    const limit = Number(this.dataset.lowStock || 0);
    const left = Number(product?.q || 0);
    badge.hidden = !(limit && left > 0 && left <= limit);
    if (!badge.hidden) badge.textContent = this.copy.lowStock.replace('__COUNT__', String(left));
  }

  openQuiz() {
    this.quiz = this.querySelector('[data-quiz]');
    if (!this.quiz) return;
    this.answers = {};
    this.quizStep(0);
    this.quiz.showModal();
  }

  quizStep(index) {
    this.quizIndex = index;
    const steps = [...this.quiz.querySelectorAll('[data-quiz-step]')];
    steps.forEach((step, i) => { step.hidden = i !== index; });
    steps[index]?.querySelector('button')?.focus();
  }

  /** Last answer: occasion picks the look, intensity moves it a step softer or bolder, skin steers the finish. */
  answerQuiz(question, answer) {
    this.answers[question] = answer;
    const steps = this.quiz.querySelectorAll('[data-quiz-step]');
    if (this.quizIndex < steps.length - 1) {
      this.quizStep(this.quizIndex + 1);
      return;
    }
    let look = QUIZ_LOOK[this.answers.occasion] || 'soft_glam';
    const rung = LADDER.indexOf(look);
    if (this.answers.intensity === 'subtle') look = look === 'party' ? 'soft_glam' : LADDER[Math.max(0, rung - 1)];
    if (this.answers.intensity === 'bold' && rung >= 0) look = LADDER[Math.min(LADDER.length - 1, rung + 1)];
    this.state.look = look;
    this.state.skin = SKIN[this.answers.skin] ? this.answers.skin : null;
    this.state.shuffle = 0;
    this.touched = false;
    this.quiz.close();
    this.syncChoices();
    this.buildKit();
    const name = this.querySelector(`[data-look="${look}"] .font-heading`)?.textContent.trim() || look;
    this.announce(this.copy.quizDone.replace('__LOOK__', name));
    this.revealTable();
  }

  /** Pieces on the table come first (in application order); empty and unavailable spots move to the end. */
  sortTrays() {
    const list = this.querySelector('[data-table]');
    if (!list) return;
    const rank = (tray) => ({ empty: 1, none: 2 })[tray.dataset.state] || 0;
    const slots = Object.keys(this.trays);
    const desired = slots.map((slot) => this.trays[slot]).sort((a, b) => rank(a) - rank(b));
    const current = [...list.children];
    if (desired.every((tray, index) => current[index] === tray)) return;
    const active = document.activeElement;
    const move = (tray) => (typeof list.moveBefore === 'function' ? list.moveBefore(tray, null) : list.append(tray));
    desired.forEach(move);
    if (active && active !== document.activeElement && list.contains(active)) active.focus({ preventScroll: true });
  }

  /** Narrows the open picker to products whose name, shade or brand has every typed word. */
  filterPicker(query) {
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    this.pickerList = words.length
      ? this.pickerAll.filter((product) => {
        const haystack = `${product.text} ${String(product.brand || '').toLowerCase()}`;
        return words.every((word) => haystack.includes(word));
      })
      : this.pickerAll;
    this.pickerShown = 0;
    this.pickerGrid.replaceChildren();
    this.picker.querySelector('[data-picker-empty]').hidden = this.pickerList.length > 0;
    this.renderPickerPage();
    this.picker.querySelector('[data-picker-scroll]').scrollTop = 0;
  }

  /** Phones: after picking a look, bring the table into view if it's below the fold. */
  revealTable() {
    if (!window.matchMedia('(max-width: 47.99rem)').matches) return;
    const table = this.querySelector('[data-table]');
    if (!table || table.getBoundingClientRect().top < window.innerHeight * 0.6) return;
    window.setTimeout(() => table.scrollIntoView({ behavior: this.reduceMotion.matches ? 'auto' : 'smooth', block: 'start' }), 150);
  }

  /** Phones: drag the sheet's handle down to close it. */
  bindSheetDrag(signal) {
    const handle = this.picker?.querySelector('[data-picker-drag]');
    if (!handle) return;
    let startY = null;
    let delta = 0;
    handle.addEventListener('touchstart', (event) => {
      startY = event.touches[0].clientY;
      delta = 0;
      this.picker.style.transition = 'none';
    }, { signal, passive: true });
    handle.addEventListener('touchmove', (event) => {
      if (startY === null) return;
      delta = Math.max(0, event.touches[0].clientY - startY);
      this.picker.style.translate = `0 ${delta}px`;
    }, { signal, passive: true });
    handle.addEventListener('touchend', () => {
      if (startY === null) return;
      startY = null;
      this.picker.style.transition = 'translate 0.2s ease';
      if (delta > 90) {
        this.picker.close();
      }
      this.picker.style.translate = '';
    }, { signal });
  }

  /** Adds the next page of options to the open picker; every in-stock product of the spot is reachable. */
  renderPickerPage() {
    const slot = this.pickerSlot;
    const list = this.pickerList || [];
    const current = this.kit[slot];
    const fragment = document.createDocumentFragment();
    const next = list.slice(this.pickerShown, this.pickerShown + PICKER_PAGE);
    for (const product of next) {
      const node = this.optionTemplate.content.firstElementChild.cloneNode(true);
      const button = node.querySelector('button');
      button.dataset.index = String(this.catalog[slot].indexOf(product));
      button.setAttribute('aria-pressed', String(product === current || product === current?.base));
      const img = node.querySelector('[data-img]');
      if (product.i) {
        img.src = product.i;
        img.alt = '';
      } else {
        img.hidden = true;
      }
      node.querySelector('[data-brand]').textContent = product.brand || '';
      node.querySelector('[data-title]').textContent = product.vt ? `${product.name} · ${product.vt}` : product.name;
      node.querySelector('[data-price]').textContent = product.pf || this.money(product.p);
      this.renderLow(node, product);
      const compare = node.querySelector('[data-compare]');
      compare.hidden = !(product.c > product.p);
      if (!compare.hidden) compare.querySelector('[data-compare-value]').textContent = this.money(product.c);
      fragment.append(node);
    }
    const firstNew = fragment.firstElementChild?.querySelector('button');
    this.pickerGrid.append(fragment);
    const firstPage = this.pickerShown === 0;
    this.pickerShown += next.length;
    const more = this.picker.querySelector('[data-picker-more]');
    if (more) more.hidden = this.pickerShown >= list.length;
    if (!firstPage) firstNew?.focus();
  }

  async addToCart(products, button) {
    // "_studio" is a hidden line property: shoppers don't see it, the order page in admin does.
    const source = `Makeup Studio · ${LOOK_LABELS[this.state.look] || 'Custom'}`;
    const items = products.filter(Boolean).map((product) => ({ id: Number(product.v), quantity: 1, properties: { _studio: source } }));
    if (!items.length || button.disabled) return;
    const label = button.querySelector('[data-label]') || button;
    const original = label.textContent;
    button.disabled = true;
    label.textContent = this.copy.adding;
    try {
      const response = await fetch(`${this.dataset.cartAdd}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) throw new Error(String(response.status));
      await this.tagCart(source, button.hasAttribute('data-add-all'));
      await this.applyKitCode(products, button);
      const CartItems = customElements.get('s-cart');
      if (CartItems && typeof CartItems.refreshMountedCarts === 'function') await CartItems.refreshMountedCarts();
      label.textContent = this.copy.added;
      this.announce(this.copy.added);
      if ('vibrate' in navigator) navigator.vibrate(12);
      openCartDrawer();
    } catch {
      label.textContent = this.copy.addError;
      this.announce(this.copy.addError);
    }
    window.setTimeout(() => {
      label.textContent = original;
      button.disabled = button.hasAttribute('data-add-all') && !Object.values(this.kit).some(Boolean);
    }, 2200);
  }

  /** A full kit (enough pieces not on sale) gets the studio-only discount code saved on the cart, the same way a shareable discount link does. */
  /** Order-level note ("Additional details" on the order): the studio was used, with the look, and whether the whole kit was added. */
  async tagCart(source, wholeKit) {
    if (!this.dataset.cartUpdate) return;
    try {
      await fetch(`${this.dataset.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { 'Makeup Studio': wholeKit ? `${source} (whole kit)` : source } }),
      });
    } catch {
      // Tracking only; never block the add.
    }
  }

  async applyKitCode(products, button) {
    if (!this.kitCode || !button.hasAttribute('data-add-all') || !this.kitMin) return;
    if (products.filter((product) => this.eligible(product)).length < this.kitMin) return;
    try {
      await fetch(`${this.dataset.discountRoot}${encodeURIComponent(this.kitCode)}`, { credentials: 'same-origin', signal: this.controller.signal });
    } catch {
      // The kit is already in the cart; she can still type the code at checkout.
    }
  }

  announce(message) {
    this.status.textContent = '';
    requestAnimationFrame(() => { this.status.textContent = message; });
  }
}

if (!customElements.get('s-makeup-studio')) customElements.define('s-makeup-studio', MakeupStudio);
